import React, {useState, useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import Separator from '../../../../../components/Simple/Sepperator';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {useTranslation} from 'react-i18next';
import SMCTable from '../../../../../components/core/SMCTable/SMCTable';
import ButtonContainer from '../../../../../components/Simple/ButtonContainer';
import {useBoolean, useSMCSnackbar} from '../../../../../utils/hooks';
import GatewayAdminEditorModal from '../../../../PopupPages/GatewayAdminEditorModal';
import SMCButton from '../../../../../components/Simple/SMCButton';
import SMCLink from '../../../../../components/Simple/SMCLink';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.administrators;
const pageId = pageSchema.pageId;

function BaseAdministrators({
    pageData,
    setObjectData,
    pageDisabled
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const managedInSmp   = pageData[pageSchema.manageInSmp];
    const administrators = pageData[pageSchema.administrators];
    
    const setPageData = useCallback((paramName, newValue) => {
        if (!pageDisabled) {
            setObjectData([pageId, paramName], newValue);
        }
    }, [pageDisabled, setObjectData]);

    const [isAdministratorEditorOpen, openAdministratorEditor, closeAdministratorEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [editObject, setEditObject] = useState(null);
    const [editObjectIndex, setEditObjectIndex] = useState(null);

    const deleteSelectedAdmins = useCallback(() => {
        if (administrators && administrators.length > 1 && selectedObjects.length > 0) {
            const newAdminObject = [...administrators];
            // Start at index 1 because can't delete first object
            for (let i = 1; i < newAdminObject.length; i++) {
                const objName = newAdminObject[i][pageSchema.administratorsSchema.username];
                if (selectedObjects.indexOf(objName) !== -1) {
                    newAdminObject.splice(i, 1);
                    i--;
                }
            }
            setSelectedObjects([]);
            setPageData(pageSchema.administrators, newAdminObject);
        }
    }, [administrators, selectedObjects, setPageData]);

    const administratorEditorOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectIndex(null);
        closeAdministratorEditor();
    }, [closeAdministratorEditor]);

    const newAdmininstratorCallback = useCallback((newAdminObject) => {
        if (administrators && administrators.find(admin => admin[pageSchema.administratorsSchema.username] === newAdminObject.username)) {
            enqueueErrorSnackbar('The ID "' + newAdminObject.username + '" is already in use.', {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
        } else {
            const adminCopy = administrators ? [...administrators] : [];
            adminCopy.push({
                [pageSchema.administratorsSchema.username]: newAdminObject.username,
                [pageSchema.administratorsSchema.password]: newAdminObject.password,
                [pageSchema.administratorsSchema.role]: newAdminObject.role
            });
            setPageData(pageSchema.administrators, adminCopy);
            closeAdministratorEditor();
        }
    }, [administrators, closeAdministratorEditor, enqueueErrorSnackbar, setPageData]);

    const editAdminAccessCallback = useCallback((newAdminObject) => {
        // If name changed, check if that name exists already
        if (
            (administrators[editObjectIndex][pageSchema.administratorsSchema.username] !== newAdminObject.username) && 
            (administrators && administrators.find(admin => admin[pageSchema.administratorsSchema.username] === newAdminObject.username))
        ) {
            enqueueErrorSnackbar('The ID "' + newAdminObject.username + '" is already in use.', {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
        } else {
            const adminCopy = administrators ? [...administrators] : [];
            adminCopy[editObjectIndex] = {
                [pageSchema.administratorsSchema.username]: newAdminObject.username,
                [pageSchema.administratorsSchema.password]: newAdminObject.password,
                [pageSchema.administratorsSchema.role]: newAdminObject.role
            };
            setPageData(pageSchema.administrators, adminCopy);
            setEditObject(null);
            setEditObjectIndex(null);
            closeAdministratorEditor();
        }
    }, [administrators, closeAdministratorEditor, editObjectIndex, enqueueErrorSnackbar, setPageData]);

    const UsernameCell = React.memo(({value, object, index}) => {
        return (
            <SMCLink
                inline
                disabled={pageDisabled || !managedInSmp}
                onClick={() => {
                    setEditObject(object);
                    setEditObjectIndex(index);
                    openAdministratorEditor();
                }}
            >
                { value }
            </SMCLink>
        );
    });

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={managedInSmp}
                onChange={checked => setPageData(pageSchema.manageInSmp,checked)}
                label={t("SMP.PLAN.MANAGE_IN_SMP")}
            />
            <Separator isMainSeparator/>
            <PlanEditorInnerContainer title={t('SMP.PLAN.DS.ADMIN.GA')} managedInSmp={managedInSmp}>
                <ButtonContainer>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="new" onClick={openAdministratorEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="delete" onClick={deleteSelectedAdmins}>{t('SMC_GEN_DELETE')}</SMCButton>
                </ButtonContainer>
                <SMCTable
                    columns={[
                        {id: 'username', accessor: pageSchema.administratorsSchema.username, header: t('SMP.NAME'),       width: 175, Cell: UsernameCell },
                        {id: 'role',     accessor: pageSchema.administratorsSchema.role,     header: t('SMP.PERMISSION'), width: 175                     }
                    ]}
                    isRowSelectable={(obj, index) => (index !== 0)}
                    rowIdSelector={(obj) => (obj[pageSchema.administratorsSchema.username])}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    content={administrators}
                    width={500}
                    maxHeight={500}
                />
                <GatewayAdminEditorModal
                    isOpen={isAdministratorEditorOpen}
                    isFirstAdministrator={editObjectIndex !== null ? editObjectIndex === 0 : (!administrators || !administrators.length)}
                    onSuccess={editObject ? editAdminAccessCallback : newAdmininstratorCallback}
                    onCancel={administratorEditorOnCancel}
                    defaultObject={editObject}
                />
            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseAdministrators;